import type { ApiSchema } from "../../../api/apiSchema";

/**
 * An item's texts in both languages side by side (VF-D-03; WD-1+3 D-9): the reader's language
 * first, so neither goes stale unseen. Saving writes the item's existing translation format
 * (P-21): each language's entry is used whole, so it needs a name and a description. The
 * English column edits the item's own (source) text, and any English entry with it, so an older
 * English entry never shadows the edit; German is the German entry's.
 */
export type FormLanguage = "de" | "en";

/** Each language by its own name, as a language list shows it. */
export const LANGUAGE_NAMES: Readonly<Record<FormLanguage, string>> = { de: "Deutsch", en: "English" };

type Translation = ApiSchema<"Translation">;
export type Translations = Readonly<Record<string, Translation>>;

/** A text a translation entry can hold. */
export type TranslatedText = { readonly field: "name" | "description" | "instructions" } | { readonly field: "choice_labels"; readonly key: string };

/** The reader's language, then the other. */
export function formLanguages(reader: string | undefined): readonly [FormLanguage, FormLanguage] {
  return reader?.toLowerCase().startsWith("de") === true ? ["de", "en"] : ["en", "de"];
}

/** What the translation in `language` says for `text`; `undefined` where it says nothing. */
export function translatedText(translations: Translations | null | undefined, language: FormLanguage, text: TranslatedText): string | undefined {
  const entry = translations?.[language];
  if (entry === undefined) return undefined;
  return text.field === "choice_labels" ? (entry.choice_labels?.[text.key] ?? undefined) : (entry[text.field] ?? undefined);
}

/** The text a column shows: the translation's; else, in English, the item's own text (`source`); else nothing yet. */
export function columnText(translations: Translations | null | undefined, language: FormLanguage, text: TranslatedText, source: string): string {
  return translatedText(translations, language, text) ?? (language === "en" ? source : "");
}

/**
 * The translations after `language`'s column wrote `value` into `text`. English changes an
 * English entry only where one exists (the item's own text is the caller's to write); a German
 * entry is made with its first text and goes once it holds none. `undefined` where none is left.
 */
export function withColumnText(translations: Translations | null | undefined, language: FormLanguage, text: TranslatedText, value: string): Translations | undefined {
  const current = translations?.[language];
  if (language === "en" && current === undefined) return translations ?? undefined;
  const entry = written(current ?? { name: "", description: "" }, text, value);
  const { [language]: _replaced, ...others } = translations ?? {};
  const next = isEmpty(entry) ? others : { ...others, [language]: entry };
  return Object.keys(next).length === 0 ? undefined : next;
}

/** An item with a name and description of its own (source) and their translations: a step definition or a Workflow. */
export interface TextedItem {
  readonly name: string;
  readonly description: string;
  readonly translations?: Translations | null;
}

/** The translations to store after a column wrote `next`: where none is left, the item keeps having none as it had none, a field it never had stays away. */
export function storedTranslations(before: Translations | null | undefined, next: Translations | undefined): Translations | null | undefined {
  return next ?? (before === undefined ? undefined : null);
}

/** `item` after `language`'s column wrote its name or description: English writes the item's own text and any English entry. */
export function withItemText<T extends TextedItem>(item: T, language: FormLanguage, field: "name" | "description", value: string): T {
  const translations = storedTranslations(item.translations, withColumnText(item.translations, language, { field }, value));
  if (language !== "en") return { ...item, translations };
  return field === "name" ? { ...item, name: value, translations } : { ...item, description: value, translations };
}

/** What a reader of `language` sees where its column is blank: the item's own text, which the server falls back to (Sol 4c r1 C3); `undefined` where the column says it. */
export function columnFallback(translations: Translations | null | undefined, language: FormLanguage, text: TranslatedText, source: string): string | undefined {
  return language !== "en" && translatedText(translations, language, text) === undefined ? source : undefined;
}

/** The translations after a choice's key became `to`, or the choice went (`null`): its labels move with it or go, and an entry left with nothing goes. The caller renames only to a free key. */
export function withChoiceKey(translations: Translations | null | undefined, from: string, to: string | null): Translations | undefined {
  if (translations === null || translations === undefined) return undefined;
  const entries = Object.entries(translations).flatMap(([language, entry]): [string, Translation][] => {
    const labels = entry.choice_labels;
    if (labels === null || labels === undefined || !(from in labels)) return [[language, entry]];
    const { [from]: label, ...others } = labels;
    const next = to === null || label === undefined ? others : { ...others, [to]: label };
    const moved = { ...entry, choice_labels: Object.keys(next).length === 0 ? null : next };
    return isEmpty(moved) ? [] : [[language, moved]];
  });
  return entries.length === 0 ? undefined : Object.fromEntries(entries);
}

/** Each language's name and description only: what a step keeps when its kind changes, as the rest belongs to one kind (Sol 4c r1 C1). */
export function namesOnly(translations: Translations | null | undefined): Translations | undefined {
  if (translations === null || translations === undefined) return undefined;
  const entries = Object.entries(translations).flatMap(([language, entry]): [string, Translation][] => {
    const names = { name: entry.name, description: entry.description };
    return isEmpty(names) ? [] : [[language, names]];
  });
  return entries.length === 0 ? undefined : Object.fromEntries(entries);
}

/** The languages whose entry lacks its name or its description, which the format needs. */
export function incompleteLanguages(translations: Translations | null | undefined): FormLanguage[] {
  return (["de", "en"] as const).filter((language) => {
    const entry = translations?.[language];
    return entry !== undefined && (entry.name.trim() === "" || entry.description.trim() === "");
  });
}

function written(entry: Translation, text: TranslatedText, value: string): Translation {
  if (text.field !== "choice_labels") {
    return { ...entry, [text.field]: text.field === "instructions" && value === "" ? null : value };
  }
  const { [text.key]: _replaced, ...labels } = entry.choice_labels ?? {};
  const next = value === "" ? labels : { ...labels, [text.key]: value };
  return { ...entry, choice_labels: Object.keys(next).length === 0 ? null : next };
}

function isEmpty(entry: Translation): boolean {
  return entry.name === "" && entry.description === "" && (entry.instructions ?? null) === null && (entry.choice_labels ?? null) === null && (entry.input_labels ?? null) === null;
}
